import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useCurrentPath } from '@service-initiation/shared-data-access-flow-manager';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { Route } from '@service-initiation/tech-care-data-access-routing';
import {
  DeliveryMethod,
  deliverySelectionConfirmedAtom,
  store,
  useDeliveryMethodValue,
  useMailInValue,
  useRemoteTechValue,
  useSelectedStoreAsyncAtom,
} from '@service-initiation/tech-care-data-access-store';
import { CallButton } from '@service-initiation/tech-care-ui-call-button';
import { GetDirections } from '@service-initiation/tech-care-ui-get-directions';
import { StoreHoursButton } from '@service-initiation/tech-care-ui-store-hours-button';
import { StoreHoursModal } from '@service-initiation/tech-care-ui-store-hours-modal';

import {
  PrimaryText,
  SecondaryText,
  VisitDetailsListItem,
} from '../components';

const TEXT = TranslationEntries.Repairs.Cart.VisitDetails;
const MODAL_TEXT = TranslationEntries.Repairs.StoreHoursModal;

type Address = {
  address_1?: string;
  address_2?: string;
  city?: string;
  name?: string;
  open_times?: {
    days: string;
    hours: string;
  }[];
  postal_code?: string;
  state?: string;
};

const LinksContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 4px;
  column-gap: 16px;
  row-gap: 4px;
`;

const LOCATION_SUMMARY_ITEM_ID = 'location-summary-item';

export const LocationSummaryItem: FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const currentPath = useCurrentPath();
  const deliveryMethod = useDeliveryMethodValue();
  const deliveryConfirmed = store.get(deliverySelectionConfirmedAtom);
  const mailIn = useMailInValue();
  const remoteTech = useRemoteTechValue();
  const { value: selectedStore } = useSelectedStoreAsyncAtom();

  const getAddressByDeliveryMethod: Address = useMemo(() => {
    if (deliveryMethod === DeliveryMethod.MailIn) {
      return {
        address_1: mailIn?.shippingAddress?.streetAddress,
        address_2: mailIn?.shippingAddress?.address2,
        city: mailIn?.shippingAddress?.city,
        postal_code: mailIn?.shippingAddress?.zip,
        state: mailIn?.shippingAddress?.state,
      };
    } else if (deliveryMethod === DeliveryMethod.ComeToMe) {
      return {
        address_1: remoteTech?.meetingAddress?.streetAddress,
        address_2: remoteTech?.meetingAddress?.address2,
        city: remoteTech?.meetingAddress?.city,
        postal_code: remoteTech?.meetingAddress?.zip,
        state: remoteTech?.meetingAddress?.state,
      };
    }

    return {
      address_1: selectedStore?.address_1,
      address_2: selectedStore?.address_2,
      city: selectedStore?.city,
      name: selectedStore?.name,
      open_times: selectedStore?.hours?.human,
      postal_code: selectedStore?.postal_code,
      state: selectedStore?.state,
    };
  }, [
    mailIn?.shippingAddress?.address2,
    mailIn?.shippingAddress?.city,
    mailIn?.shippingAddress?.state,
    mailIn?.shippingAddress?.streetAddress,
    mailIn?.shippingAddress?.zip,
    remoteTech?.meetingAddress?.address2,
    remoteTech?.meetingAddress?.city,
    remoteTech?.meetingAddress?.state,
    remoteTech?.meetingAddress?.streetAddress,
    remoteTech?.meetingAddress?.zip,
    selectedStore?.address_1,
    selectedStore?.address_2,
    selectedStore?.city,
    selectedStore?.hours?.human,
    selectedStore?.name,
    selectedStore?.postal_code,
    selectedStore?.state,
    deliveryMethod,
  ]);

  return (
    <VisitDetailsListItem
      active={
        currentPath === Route.DeliverySelection ||
        !!getAddressByDeliveryMethod.address_1
      }
      iconSrc="location-pin"
      id={LOCATION_SUMMARY_ITEM_ID}
      linkTo="DeliverySelection"
      title={
        deliveryMethod === DeliveryMethod.MailIn ||
        deliveryMethod === DeliveryMethod.ComeToMe
          ? t(TEXT.MailInAddressText)
          : t(TEXT.LocationText)
      }
    >
      {modalOpen && (
        <StoreHoursModal
          data-testid="StoreHoursModal"
          header={selectedStore?.name + t(MODAL_TEXT.Header) || ''}
          isOpen={modalOpen}
          onCloseClick={() => setModalOpen(false)}
          primaryButtonLabel={t(MODAL_TEXT.CloseButton)}
          storeDatesAndHours={getAddressByDeliveryMethod.open_times}
        />
      )}
      {deliveryConfirmed && getAddressByDeliveryMethod.address_1 && (
        <PrimaryText
          className={
            deliveryMethod !== DeliveryMethod.VisitStore ? 'fs-mask' : undefined
          }
        >
          {deliveryMethod === DeliveryMethod.VisitStore &&
          !!getAddressByDeliveryMethod?.name
            ? `${getAddressByDeliveryMethod?.name} Store`
            : getAddressByDeliveryMethod?.address_1}
          <SecondaryText>
            <br />
            {deliveryMethod === DeliveryMethod.VisitStore && (
              <>
                {getAddressByDeliveryMethod?.address_1}
                <br />
              </>
            )}
            {getAddressByDeliveryMethod?.address_2 && (
              <>
                {getAddressByDeliveryMethod?.address_2}
                <br />
              </>
            )}
            {`${getAddressByDeliveryMethod?.city}, ${getAddressByDeliveryMethod?.state} ${getAddressByDeliveryMethod?.postal_code}`}
          </SecondaryText>
          {deliveryMethod === DeliveryMethod.VisitStore && (
            <LinksContainer>
              <GetDirections address={selectedStore?.address} variant="link" />
              <CallButton
                contactNumber={selectedStore?.phone}
                label={selectedStore?.phone}
                variant="link"
              />
              {getAddressByDeliveryMethod.open_times && (
                <StoreHoursButton setModalOpen={setModalOpen} variant="link" />
              )}
            </LinksContainer>
          )}
        </PrimaryText>
      )}
    </VisitDetailsListItem>
  );
};
